import { EmailContentProps } from './session-email-content';
import AddToCalendar from './add-to-calendar';
import { JSX } from 'react';
import { namePlaceholder, regCodePlaceholder } from './event-email';
import { EmailTextPhrases, EmailTypeEnum } from '@/types/email.types';
import { getDefaultPlainTextProjectEmail } from '../helpers/get-default-plain-text-project-email';
import { TextAlignmentsEnum } from '@/types/template-layouts.types';
import useEditableEmailFields from '../helpers/use-editable-email-fields';
import OptionalComponent from '@ui/optional-component/optional-component';

interface IEditableContent {
  salutation: JSX.Element | string;
  eventName: JSX.Element | string;
  eventDescription: JSX.Element | string;
  eventStart: JSX.Element | string;
}

const EventEmailContent = ({
  emailType,
  design,
  emailContent,
  customContent,
  onEdit,
  sent,
  forceRtl,
  hideCalendar,
}: EmailContentProps): JSX.Element => {
  const textPhrase = (key: keyof Omit<EmailTextPhrases, 'subject'>) => {
    return emailContent.translations[key];
  };
  const isRegistrationConfirmationEmail = emailType === EmailTypeEnum.event_registration_email;

  // registration uses personalized greeting, event email does not
  const salutationText = isRegistrationConfirmationEmail
    ? `${textPhrase('salutation2')} ${namePlaceholder}, ${textPhrase('registered')}`
    : `${textPhrase('salutation2')}, ${textPhrase('invited')}`;

  const buttonText = textPhrase(
    !isRegistrationConfirmationEmail && emailContent?.event?.registrationRequired ? 'RegisterNow' : 'SessionDetails'
  );

  const editableContent: IEditableContent = {
    salutation: (
      <h4 style={design.styles.h4}>
        <span style={design.styles.h4Span}>{salutationText}</span>
      </h4>
    ),
    eventName: (
      <h3 style={design.styles.h3}>
        <span style={design.styles.h3Span}>{emailContent?.event?.name}</span>
      </h3>
    ),
    eventDescription: (
      <p style={design.styles.p}>
        <span style={design.styles.pSpan}>{emailContent?.event?.description}</span>
      </p>
    ),
    eventStart: (
      <h6 style={design.styles.h6}>
        <span style={design.styles.h6Span}>{textPhrase('eventStart')}</span>
      </h6>
    ),
  };

  const { getCellContent, getButton, getImage } = useEditableEmailFields<IEditableContent>({
    onEdit,
    sent,
    design,
    emailContent,
    emailContentType: 'event',
    editableContent,
    customContent,
    buttonText,
  });

  const alignment = (ltrLanguageAlign = TextAlignmentsEnum.center) =>
    forceRtl ? TextAlignmentsEnum.right : ltrLanguageAlign;
  const direction = forceRtl ? 'rtl' : 'ltr';

  if (emailContent?.settings?.usePlainTextEmail) {
    const calendarLinkAttachments =
      emailContent.settings?.showAddCalendar && emailContent?.event?.icsFileContent?.icsFileText && !hideCalendar
        ? [
            {
              name: 'cal.ics',
              content: emailContent.event.icsFileContent.icsFileText,
              contentId: 'cal',
              contentType: 'text/calendar; charset=utf-8; method=REQUEST',
              disposition: 'inline',
            },
          ]
        : [];

    const eventUrl = `${emailContent.event?.url}${regCodePlaceholder}`;

    if (!emailContent.event) return <></>;
    return (
      <tr>
        <td align={alignment(TextAlignmentsEnum.left)} dir={direction}>
          <textarea
            autoFocus // DO NOT REMOVE autoFocus, we need this to initialize the plainTextArea content in working_customizations by triggering onBlur
            rows={33}
            disabled={sent}
            style={{ width: '100%', resize: 'none', whiteSpace: 'pre-wrap' }}
            onBlur={(e) => {
              if (!sent) {
                const val = e.target.value.replace(eventUrl, eventUrl);
                onEdit?.(val, 'plainTextEmail', { attachments: calendarLinkAttachments, eventUrl });
              }
            }}
            defaultValue={
              (customContent?.plainTextEmail as string | undefined) ??
              getDefaultPlainTextProjectEmail({
                salutation: salutationText,
                projectName: emailContent.event.name,
                projectDescription: emailContent.event.description,
                projectStart: textPhrase('eventStart'),
                projectStartTime: emailContent.event.start || '',
                emailContent,
                emailType,
                hideCalendar,
              })
            }
          />
        </td>
      </tr>
    );
  }
  return (
    <>
      <tr>{getImage()}</tr>
      <tr>
        <td align={alignment(TextAlignmentsEnum.left)} dir={direction}>
          {getCellContent('salutation')}
        </td>
      </tr>
      <tr>
        <td align={alignment(TextAlignmentsEnum.left)} dir={direction}>
          {getCellContent('eventName')}
        </td>
      </tr>
      <tr>
        <td align={alignment(TextAlignmentsEnum.left)} dir={direction}>
          {getCellContent('eventDescription')}
        </td>
      </tr>
      <OptionalComponent display={!!emailContent?.event?.start && emailContent.settings?.showStartTime}>
        <>
          <tr className="bl-event-showtime">
            <td align={alignment(TextAlignmentsEnum.left)} dir={direction}>
              {getCellContent('eventStart')}
            </td>
          </tr>
          <tr className="bl-event-showtime">
            <td align={alignment()} dir={direction}>
              <p style={{ ...design.styles.accent, ...design.styles.accentSpan }}>{emailContent?.event?.start}</p>
            </td>
          </tr>
        </>
      </OptionalComponent>
      <tr style={{ height: '55px' }}>
        <td align={alignment()} dir={direction}>
          {getButton()}
        </td>
      </tr>
      <OptionalComponent display={emailContent.settings?.showAddCalendar && !hideCalendar}>
        <AddToCalendar
          emailContent={emailContent}
          design={design}
          emailType={emailType}
          customContent={customContent}
          onEdit={onEdit}
          sent={sent}
        />
      </OptionalComponent>
    </>
  );
};

export default EventEmailContent;
